/**
 * Which of the Fleet-convergence card's states applies — as a pure function, so
 * the ORDER of the checks is provable rather than buried in a ternary chain.
 *
 * WHY THIS IS NOT INLINE: the bug it fixes was an ordering/omission bug. The card
 * used to reach `total === 0` without ever asking whether the read had SUCCEEDED,
 * so four different situations rendered the same confident sentence — "No azure
 * runtimes registered". Observed on PROD 2026-08-07 (`lexiun-ws-box`): the card
 * declared the fleet empty while `/portal/runtimes` listed five healthy azure
 * rows at the same moment.
 *
 * Three of those four states are "I could not look":
 *
 *   initializing  the first poll has not resolved — nothing is known yet
 *   !available    the batch endpoint is absent/unreachable (404 or network)
 *   error         the endpoint exists and faulted (401/403/500)
 *
 * and exactly one is "I looked and the answer is zero". Rendering them alike is
 * the `unknown-must-be-representable` failure: a failed READ presented as a
 * VALUE. The precedence below is the fix and is asserted by the tests —
 * "not looked yet" and "could not look" both outrank any count, because a count
 * derived from an unread map is not evidence of anything.
 */

export type ConvergenceState =
  /** No active bundle to converge against — the card's pre-existing first branch. */
  | { kind: "no-active-tag" }
  /** First poll unresolved. Assert nothing about the fleet. */
  | { kind: "loading" }
  /** Read failed. `reason` distinguishes an absent endpoint from a fault. */
  | { kind: "unreadable"; reason: "endpoint-unreachable" | "endpoint-faulted"; detail: string | null }
  /** A SUCCESSFUL read that genuinely found no azure runtimes. */
  | { kind: "empty-fleet" }
  /** A successful read with rows to report. */
  | { kind: "ready" };

export interface ConvergenceInputs {
  /** False when there is no active bundle tag to converge against. */
  hasActiveTag: boolean;
  /** `useRuntimeVersions().initializing` — true until the FIRST poll resolves. */
  initializing: boolean;
  /** `useRuntimeVersions().available` — false ⇒ endpoint not live/reachable. */
  available: boolean;
  /** `useRuntimeVersions().error` — set only when the endpoint genuinely faulted. */
  error: string | null;
  /** Azure runtimes counted from the version map. Meaningless unless the read succeeded. */
  total: number;
}

export function selectConvergenceState(i: ConvergenceInputs): ConvergenceState {
  if (!i.hasActiveTag) return { kind: "no-active-tag" };

  // ⚠ Both of these MUST precede any use of `total`. `total` is derived from a
  // map that is empty both when the fleet is empty and when nothing was read.
  if (i.initializing) return { kind: "loading" };
  if (!i.available) {
    return { kind: "unreadable", reason: "endpoint-unreachable", detail: i.error };
  }
  if (i.error) {
    return { kind: "unreadable", reason: "endpoint-faulted", detail: i.error };
  }

  return i.total === 0 ? { kind: "empty-fleet" } : { kind: "ready" };
}
